import React, { useMemo, useState } from "react";
import { ChevronDown, ChevronRight, Trash2, Check, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useRole } from "../context/RoleContext";
import { OfciActions, OfciBadge, OfciDialogs } from "./OfciVerifyDialog";

const LINE_TONE = {
  "Not On Hand": "bg-amber-100 text-amber-800",
  "Not Ordered": "bg-red-100 text-red-800",
  "Partially Pulled": "bg-blue-100 text-blue-800",
  Pulled: "bg-emerald-100 text-emerald-800",
  Delivered: "bg-emerald-100 text-emerald-800",
};
const roomLabel = (r = {}) =>
  [r.room_number, r.room_name].filter(Boolean).join(" · ") || "room";

// RA > Material — the creator's view of a ticket: scope → room → product lines,
// with the quantity editable and lines removable while the material is still ours.
export const MaterialTicketLines = ({ ticket, lines = [], actor, canEdit, onChanged }) => {
  const [openScopes, setOpenScopes] = useState({});
  const [openRooms, setOpenRooms] = useState({});
  const [editing, setEditing] = useState({});      // line_id -> qty being typed
  const [busy, setBusy] = useState("");
  const [ofci, setOfci] = useState(null);      // FLD-10 — verify owner-furnished material from the requester's view
  const { role } = useRole();
  const ofciTarget = (l) => {
    const req = Number(l.requested_qty || 0), ver = Number(l.ofci_verified_qty || 0);
    return { ticketId: ticket.id, projectId: ticket.project_id, line_ids: [l.id], product: l.product?.product_short || l.product?.product_code || "—",
             description: l.product?.description, requested: req, verified: ver, remaining: Math.max(req - ver, 0), ofci: true,
             ofci_source: l.ofci_source, ofci_remaining: Math.max(req - ver, 0) };
  };

  const scopes = useMemo(() => {
    const by = new Map();
    lines.forEach((l) => {
      const scope = l.scope_type || "Unassigned scope";
      if (!by.has(scope)) by.set(scope, new Map());
      const rooms = by.get(scope);
      const key = l.room?.key || roomLabel(l.room);
      if (!rooms.has(key)) rooms.set(key, { room: l.room || {}, items: [] });
      rooms.get(key).items.push(l);
    });
    return [...by.entries()].sort((a, b) => a[0].localeCompare(b[0])).map(([scope, rooms]) => {
      const roomList = [...rooms.values()].sort((a, b) =>
        String(a.room.room_number || "").localeCompare(String(b.room.room_number || "")));
      const all = roomList.flatMap((r) => r.items);
      return {
        scope,
        rooms: roomList,
        requested: all.reduce((a, l) => a + Number(l.requested_qty || 0), 0),
        pulled: all.reduce((a, l) => a + Number(l.pulled_qty || 0), 0),
        delivered: all.reduce((a, l) => a + Number(l.delivered_qty || 0), 0),
      };
    });
  }, [lines]);
  const single = scopes.length <= 1;
  const scopeOpen = (s) => single || !!openScopes[s];

  const saveQty = async (l) => {
    const qty = Number(editing[l.id]);
    if (!Number.isFinite(qty) || qty <= 0) { toast.info("Enter a quantity above zero"); return; }
    setBusy(l.id);
    try {
      await api.patch(`/material/tickets/${ticket.id}/lines/${l.id}`,
        { requested_qty: qty, actor });
      toast.success("Quantity updated");
      setEditing((c) => ({ ...c, [l.id]: undefined }));
      onChanged?.();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === "string" ? d : d?.message || "Could not change the quantity");
    } finally { setBusy(""); }
  };

  const removeLine = async (l) => {
    setBusy(l.id);
    try {
      const { data } = await api.delete(`/material/tickets/${ticket.id}/lines/${l.id}`,
        { params: { actor } });
      toast.success(data.ticket_deleted ? "Last line removed — the ticket is gone" : "Line removed",
        { description: data.returned_to_on_hand
          ? `${data.returned_to_on_hand} unit(s) went back on hand` : undefined });
      onChanged?.();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === "string" ? d : d?.message || "Could not remove the line");
    } finally { setBusy(""); }
  };

  if (lines.length === 0) {
    return <div className="py-2 text-[11px] text-gray-400">No lines on this ticket.</div>;
  }

  return (
    <>
    <OfciDialogs ofci={ofci} actor={actor} onClose={() => setOfci(null)} onDone={() => onChanged?.()} />
    <table className="w-full text-[11px]" data-testid={`material-lines-${ticket.ticket_number}`}>
      <thead className="text-[10px] uppercase text-gray-500">
        <tr>
          <th className="p-1 w-5"></th>
          <th className="text-left p-1">Scope / room / product</th>
          <th className="text-right p-1">Requested</th>
          <th className="text-right p-1">Pulled</th>
          <th className="text-right p-1">Still to pull</th>
          <th className="text-right p-1">Delivered</th>
          <th className="text-left p-1 pl-2">Line status</th>
          <th className="text-left p-1 pl-2">Shortfall</th>
          <th className="text-left p-1">PO · expected</th>
          <th className="text-left p-1">Need by</th>
          <th className="p-1"></th>
        </tr>
      </thead>
      <tbody>
        {scopes.map((s) => (
          <React.Fragment key={s.scope}>
            {!single && (
              <tr className="border-t border-gray-200 bg-blue-50/60 cursor-pointer"
                  onClick={() => setOpenScopes((c) => ({ ...c, [s.scope]: !c[s.scope] }))}
                  data-testid={`material-scope-${s.scope}`}>
                <td className="p-1">
                  {scopeOpen(s.scope) ? <ChevronDown className="w-3.5 h-3.5" />
                    : <ChevronRight className="w-3.5 h-3.5" />}
                </td>
                <td className="p-1 font-semibold uppercase tracking-wide text-[10px] text-blue-900">
                  {s.scope} <span className="font-normal normal-case text-gray-500">
                    · {s.rooms.length} room(s)</span>
                </td>
                <td className="p-1 text-right tabular-nums font-semibold">{s.requested}</td>
                <td className="p-1 text-right tabular-nums">{s.pulled}</td>
                <td className="p-1 text-right tabular-nums font-semibold">
                  {Math.max(s.requested - s.pulled, 0)}
                </td>
                <td className="p-1 text-right tabular-nums">{s.delivered}</td>
                <td className="p-1" colSpan={5}></td>
              </tr>
            )}
            {scopeOpen(s.scope) && s.rooms.map((rm) => {
              const key = `${s.scope}|${rm.room?.key || roomLabel(rm.room)}`;
              const roomOpen = !!openRooms[key];
              const req = rm.items.reduce((a, l) => a + Number(l.requested_qty || 0), 0);
              const pul = rm.items.reduce((a, l) => a + Number(l.pulled_qty || 0), 0);
              const del = rm.items.reduce((a, l) => a + Number(l.delivered_qty || 0), 0);
              return (
                <React.Fragment key={key}>
                  <tr className="border-t border-gray-200 cursor-pointer hover:bg-gray-50"
                      onClick={() => setOpenRooms((c) => ({ ...c, [key]: !c[key] }))}
                      data-testid={`material-room-${rm.room?.room_number || "room"}`}>
                    <td className="p-1">
                      {roomOpen ? <ChevronDown className="w-3.5 h-3.5" />
                        : <ChevronRight className="w-3.5 h-3.5" />}
                    </td>
                    <td className={`p-1 font-semibold ${single ? "" : "pl-4"}`}>
                      {rm.room?.building ? `${rm.room.building} ` : ""}{roomLabel(rm.room)}
                      <span className="ml-1 font-normal text-gray-500">
                        · {rm.items.length} product(s)
                      </span>
                    </td>
                    <td className="p-1 text-right tabular-nums">{req}</td>
                    <td className="p-1 text-right tabular-nums">{pul}</td>
                    <td className="p-1 text-right tabular-nums">{Math.max(req - pul, 0)}</td>
                    <td className="p-1 text-right tabular-nums">{del}</td>
                    <td className="p-1" colSpan={5}></td>
                  </tr>
                  {roomOpen && rm.items.map((l) => {
                    const name = l.product?.product_short || l.product?.product_code || "—";
                    const isEditing = editing[l.id] !== undefined;
                    return (
                      <tr key={l.id} className="bg-gray-50/80"
                          data-testid={`material-line-${l.id}`}>
                        <td className="p-1"></td>
                        <td className="p-1 pl-8 font-mono" title={l.product?.description}>{name}{l.ofci && <> <OfciBadge status={l.ofci_source === "abs_warehouse" ? "OFCI · ABS custody" : "OFCI"} testId={`material-ofci-badge-${l.id}`} /></>}</td>
                        <td className="p-1 text-right">
                          {isEditing ? (
                            <span className="inline-flex items-center gap-1">
                              <Input type="number" min="1" value={editing[l.id]} autoFocus
                                     className="h-6 w-16 text-xs text-right"
                                     data-testid={`material-qty-input-${l.id}`}
                                     onChange={(ev) => setEditing((c) => ({ ...c, [l.id]: ev.target.value }))} />
                              <button disabled={busy === l.id} onClick={() => saveQty(l)}
                                      data-testid={`material-qty-save-${l.id}`}
                                      className="text-emerald-700"><Check className="w-3.5 h-3.5" /></button>
                              <button onClick={() => setEditing((c) => ({ ...c, [l.id]: undefined }))}
                                      data-testid={`material-qty-cancel-${l.id}`}
                                      className="text-gray-500"><X className="w-3.5 h-3.5" /></button>
                            </span>
                          ) : (
                            <button disabled={!canEdit}
                                    onClick={() => setEditing((c) => ({ ...c, [l.id]: String(l.requested_qty) }))}
                                    title={canEdit ? "Change the requested quantity" : ""}
                                    data-testid={`material-qty-${l.id}`}
                                    className={`tabular-nums ${canEdit ? "underline decoration-dotted" : ""}`}>
                              {l.requested_qty}
                            </button>
                          )}
                        </td>
                        <td className="p-1 text-right tabular-nums">{l.ofci && l.ofci_source !== "abs_warehouse" ? <span className="text-gray-400" title="Owner-furnished — not pulled by ABS">—</span> : l.pulled_qty}</td>
                        <td className="p-1 text-right tabular-nums font-semibold">
                          {l.ofci && l.ofci_source !== "abs_warehouse"
                            ? <span className="text-[10px] font-normal text-amber-900" data-testid={`material-ofci-verified-${l.id}`}>verified {Number(l.ofci_verified_qty || 0)}/{l.requested_qty}</span>
                            : Math.max(Number(l.requested_qty || 0) - Number(l.ofci_verified_qty || 0) - Number(l.pulled_qty || 0), 0)}
                        </td>
                        <td className="p-1 text-right tabular-nums">{l.delivered_qty}</td>
                        <td className="p-1 pl-2">
                          {l.ofci ? <OfciBadge status={l.ofci_status} testId={`material-ofci-status-${l.id}`} /> : (
                            <span className={`px-1 rounded text-[10px] font-semibold ${LINE_TONE[l.line_status] || "bg-gray-100 text-gray-700"}`}>
                              {l.line_status}
                            </span>
                          )}
                          {l.ofci && Number(l.ofci_missing_qty || 0) > 0 && (
                            <span className="ml-1 text-[10px] text-red-700 font-semibold" data-testid={`material-ofci-missing-${l.id}`}>{Number(l.ofci_missing_qty)} missing on site</span>
                          )}
                          {l.frs_review && (
                            <span className="ml-1 px-1 rounded text-[10px] font-semibold bg-amber-100 text-amber-800">
                              {l.frs_review}
                            </span>
                          )}
                        </td>
                        <td className="p-1 pl-2">
                          {l.shortfall_reason ? (
                            <span className="px-1 rounded text-[10px] font-semibold bg-amber-100 text-amber-800"
                                  title={l.substitute_product ? `Substitute: ${l.substitute_product}` : l.pull_note || ""}
                                  data-testid={`material-line-shortfall-${l.id}`}>
                              {String(l.shortfall_reason).replace(/_/g, " ")}
                              {l.substitute_product ? ` → ${l.substitute_product}` : ""}
                            </span>
                          ) : "—"}
                        </td>
                        <td className="p-1">
                          {l.po_ref || "—"}{l.expected_arrival ? ` · ${l.expected_arrival}` : ""}
                        </td>
                        <td className="p-1">{l.need_by || "—"}</td>
                        <td className="p-1 text-right">
                          {l.ofci && (
                            <span className="mr-1"><OfciActions t={ofciTarget(l)} role={role} onVerify={(t) => setOfci({ kind: "verify", target: t })} onCustody={(t) => setOfci({ kind: "custody", target: t })} /></span>
                          )}
                          {canEdit && (
                            <button disabled={busy === l.id} onClick={() => removeLine(l)}
                                    title="Take this line off the ticket"
                                    data-testid={`material-line-remove-${l.id}`}
                                    className="text-red-600 disabled:opacity-40">
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </React.Fragment>
              );
            })}
          </React.Fragment>
        ))}
      </tbody>
    </table>
    </>
  );
};

export default MaterialTicketLines;
